import { z } from 'zod'
import { SuccessEnvelope, Uuid } from './common.js'

// ─── Enums (kept in sync with the Joi validation + DB intake JSON shape) ──────

export const DeepReportStage = z.enum(['idea', 'prototype', 'pilot', 'revenue'])

export const DeepReportBusinessModel = z.enum([
  'b2c',
  'b2b',
  'b2b2c',
  'b2g',
  'marketplace',
  'saas',
  'deep-tech',
  'other',
])

export const DeepReportCapitalBucket = z.enum([
  'none',
  'under-25l',
  '25l-1cr',
  '1cr-5cr',
  'over-5cr',
])

export const DeepReportFounderTag = z.enum([
  'technical',
  'domain-expert',
  'commercial',
  'first-time',
  'repeat-founder',
])

export const DeepReportCurrency = z.enum(['INR', 'USD'])

export const DeepReportStatus = z.enum([
  'awaiting_payment',
  'awaiting_intake',
  'queued',
  'generating',
  'ready',
  'degraded',
  'failed',
])

// ─── Intake form ──────────────────────────────────────────────────────────────
// The founder-supplied context that augments the submit-idea fields. Collected
// once when the deep report is requested (PLAN §7). India-only v1: country
// defaults to "India".

export const DeepReportFounder = z.object({
  name: z.string().min(1).max(120),
  bio: z.string().max(300).nullable().optional(),
})

export const DeepReportIntake = z
  .object({
    geography: z.object({
      country: z.string().min(1).max(80),
      city: z.string().max(80).nullable().optional(),
    }),
    stage: DeepReportStage,
    business_model: DeepReportBusinessModel,
    is_solo: z.boolean(),
    founders: z.array(DeepReportFounder).max(8),
    capital_raised_bucket: DeepReportCapitalBucket,
    runway_months: z.number().int().min(0).max(120).nullable().optional(),
    founder_background_tags: z.array(DeepReportFounderTag).max(5),
    currency: DeepReportCurrency,
    additional_context: z.string().max(500).nullable().optional(),
  })
  // A non-solo founder must list >= 1 co-founder; a solo founder must list none.
  .refine(
    (d) => (d.is_solo ? d.founders.length === 0 : d.founders.length >= 1),
    {
      message:
        'founders must be non-empty when is_solo is false, empty when true',
      path: ['founders'],
    },
  )

// ─── Responses ──────────────────────────────────────────────────────────────

// What we expose about a deep report to the owning founder. Section content is
// fetched separately once status is ready/degraded.
export const DeepReportSummary = z.object({
  id: Uuid,
  idea_id: Uuid,
  version: z.number().int(),
  status: DeepReportStatus,
  intake: DeepReportIntake.nullable(),
  paid_at: z.union([z.string(), z.date()]).nullable().optional(),
  generation_completed_at: z
    .union([z.string(), z.date()])
    .nullable()
    .optional(),
  pdf_url: z.string().nullable().optional(),
  created_at: z.union([z.string(), z.date()]).optional(),
})

export const DeepReportIntakeResponse = SuccessEnvelope(DeepReportSummary)

// Eligibility check the frontend calls before showing the intake form / CTA.
export const DeepReportEligibility = z.object({
  eligible: z.boolean(),
  // 'ok' | 'not_owner' | 'validation_open' | 'idea_not_found'
  reason: z.string(),
  validation_closed: z.boolean(),
  review_closed_at: z.union([z.string(), z.date()]).nullable().optional(),
  // The most recent report for this idea, if any (so the UI can offer
  // "view existing" vs "buy v2").
  existing_report: DeepReportSummary.nullable(),
})

export const DeepReportEligibilityResponse = SuccessEnvelope(
  DeepReportEligibility,
)
